/**
 * FundamentalNodeService.js
 * Service for fetching real fundamental/market data
 * Uses CoinGecko for crypto, and provides stock data structure
 */

// Crypto symbol to CoinGecko ID mapping
const cryptoIdMap = {
  'BTC': 'bitcoin',
  'ETH': 'ethereum',
  'SOL': 'solana',
  'ADA': 'cardano',
  'DOT': 'polkadot',
  'AVAX': 'avalanche-2',
  'MATIC': 'matic-network',
  'LINK': 'chainlink',
  'XRP': 'ripple',
  'DOGE': 'dogecoin',
  'SHIB': 'shiba-inu',
  'ATOM': 'cosmos',
  'LTC': 'litecoin',
  'TRX': 'tron',
  'BNB': 'binancecoin',
};

// Check if symbol is crypto
const isCrypto = (symbol) => {
  const cleanSymbol = symbol.replace('-USDT', '').replace('/USDT', '').toUpperCase();
  return cryptoIdMap[cleanSymbol] !== undefined;
};

// Fetch real crypto data from CoinGecko
const fetchCryptoData = async (symbol) => {
  const cleanSymbol = symbol.replace('-USDT', '').replace('/USDT', '').toUpperCase();
  const coinId = cryptoIdMap[cleanSymbol] || cleanSymbol.toLowerCase();
  
  try {
    // Fetch coin data from CoinGecko
    const response = await fetch(
      `https://api.coingecko.com/api/v3/coins/${coinId}?localization=false&tickers=false&community_data=true&developer_data=false&sparkline=false`
    );
    
    if (!response.ok) {
      throw new Error('CoinGecko API error');
    }
    
    const data = await response.json();
    
    return {
      symbol: cleanSymbol,
      name: data.name || cleanSymbol,
      financials: {
        marketCap: {
          value: formatLargeNumber(data.market_data?.market_cap?.usd || 0),
          change: (data.market_data?.market_cap_change_percentage_24h || 0).toFixed(2),
          unit: '',
          period: '24h'
        },
        volume24h: {
          value: formatLargeNumber(data.market_data?.total_volume?.usd || 0),
          change: '0',
          unit: '',
          period: '24h'
        },
        circulatingSupply: {
          value: formatLargeNumber(data.market_data?.circulating_supply || 0),
          change: '0',
          unit: cleanSymbol,
          period: 'Current'
        },
        totalSupply: {
          value: formatLargeNumber(data.market_data?.total_supply || 0),
          change: '0',
          unit: cleanSymbol,
          period: 'Max'
        },
        ath: {
          value: (data.market_data?.ath?.usd || 0).toFixed(2),
          change: (data.market_data?.ath_change_percentage?.usd || 0).toFixed(2),
          unit: 'USD',
          period: data.market_data?.ath_date?.usd?.split('T')[0] || 'N/A'
        },
        atl: {
          value: (data.market_data?.atl?.usd || 0).toFixed(6),
          change: (data.market_data?.atl_change_percentage?.usd || 0).toFixed(2),
          unit: 'USD',
          period: data.market_data?.atl_date?.usd?.split('T')[0] || 'N/A'
        },
        priceChange24h: {
          value: (data.market_data?.price_change_percentage_24h || 0).toFixed(2),
          change: '0',
          unit: '%',
          period: '24h'
        }
      },
      news: [], // Would need news API
      sentimentAnalysis: {
        overallSentiment: {
          score: ((data.sentiment_votes_up_percentage || 50) / 100).toFixed(2),
          change: '0'
        },
        sources: {
          community: ((data.sentiment_votes_up_percentage || 50) / 100).toFixed(2),
          social: ((data.community_data?.twitter_followers || 0) > 100000 ? 0.7 : 0.5).toFixed(2),
          developer: ((data.developer_score || 50) / 100).toFixed(2),
          liquidity: ((data.liquidity_score || 50) / 100).toFixed(2)
        },
        trends: []
      },
      ratings: {
        consensus: data.coingecko_rank < 50 ? 'Buy' : data.coingecko_rank < 200 ? 'Hold' : 'Speculative',
        targetPrice: {
          average: 'N/A',
          high: (data.market_data?.high_24h?.usd || 0).toFixed(2),
          low: (data.market_data?.low_24h?.usd || 0).toFixed(2),
          current: (data.market_data?.current_price?.usd || 0).toFixed(2)
        },
        distribution: {
          buy: Math.round(data.sentiment_votes_up_percentage || 50),
          hold: Math.round((100 - (data.sentiment_votes_up_percentage || 50)) / 2),
          sell: Math.round((100 - (data.sentiment_votes_up_percentage || 50)) / 2)
        },
        priceTargets: []
      },
      valuation: {
        marketCapRank: data.market_cap_rank || 'N/A',
        coingeckoRank: data.coingecko_rank || 'N/A',
        coingeckoScore: (data.coingecko_score || 0).toFixed(1),
        liquidityScore: (data.liquidity_score || 0).toFixed(1),
        communityScore: (data.community_score || 0).toFixed(1),
        fullyDilutedValuation: formatLargeNumber(data.market_data?.fully_diluted_valuation?.usd || 0),
        totalValueLocked: formatLargeNumber(data.market_data?.total_value_locked?.usd || 0),
        fdvToTvlRatio: (data.market_data?.fdv_to_tvl_ratio || 0).toFixed(2)
      },
      peerComparison: await fetchCryptoPeers(cleanSymbol),
      updatedAt: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error fetching crypto data:', error);
    // Return fallback data
    return getFallbackCryptoData(cleanSymbol);
  }
};

// Format large numbers
const formatLargeNumber = (num) => {
  if (num >= 1e12) return (num / 1e12).toFixed(2) + 'T';
  if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
  if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
  if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
  return num.toFixed(2);
};

// Fetch peer comparison for crypto
const fetchCryptoPeers = async (symbol) => {
  const peerMap = {
    'BTC': ['ETH', 'SOL', 'BNB', 'XRP'],
    'ETH': ['BTC', 'SOL', 'ADA', 'DOT'],
    'SOL': ['ETH', 'ADA', 'AVAX', 'DOT'],
    'ATOM': ['DOT', 'AVAX', 'SOL', 'ADA'],
    'LINK': ['DOT', 'ATOM', 'AVAX', 'SOL'],
  };
  
  const peers = peerMap[symbol] || ['BTC', 'ETH', 'SOL', 'BNB'];
  
  try {
    const peerIds = peers.map(p => cryptoIdMap[p] || p.toLowerCase()).join(',');
    const response = await fetch(
      `https://api.coingecko.com/api/v3/simple/price?ids=${peerIds}&vs_currencies=usd&include_market_cap=true&include_24hr_change=true`
    );
    
    if (!response.ok) return getDefaultPeers(peers);
    
    const data = await response.json();
    
    return peers.map(peer => {
      const coinId = cryptoIdMap[peer] || peer.toLowerCase();
      const coinData = data[coinId] || {};
      return {
        symbol: peer,
        name: peer,
        peRatio: 'N/A',
        marketCap: formatLargeNumber(coinData.usd_market_cap || 0),
        priceChange: (coinData.usd_24h_change || 0).toFixed(2)
      };
    });
  } catch (error) {
    return getDefaultPeers(peers);
  }
};

// Fallback crypto data
const getFallbackCryptoData = (symbol) => ({
  symbol,
  name: symbol,
  financials: {
    marketCap: { value: 'Loading...', change: '0', unit: '', period: '' },
    volume24h: { value: 'Loading...', change: '0', unit: '', period: '' },
    circulatingSupply: { value: 'Loading...', change: '0', unit: '', period: '' },
    totalSupply: { value: 'Loading...', change: '0', unit: '', period: '' },
    ath: { value: 'N/A', change: '0', unit: '', period: '' },
    atl: { value: 'N/A', change: '0', unit: '', period: '' },
    priceChange24h: { value: '0', change: '0', unit: '%', period: '24h' }
  },
  news: [],
  sentimentAnalysis: { overallSentiment: { score: '0.5', change: '0' }, sources: {}, trends: [] },
  ratings: { consensus: 'N/A', targetPrice: {}, distribution: { buy: 0, hold: 0, sell: 0 }, priceTargets: [] },
  valuation: {},
  peerComparison: [],
  updatedAt: new Date().toISOString()
});

const getDefaultPeers = (peers) => peers.map(p => ({
  symbol: p,
  name: p,
  peRatio: 'N/A',
  marketCap: 'N/A',
  priceChange: '0'
}));

// Main fetch function - routes to crypto or stock data
const fetchFundamentalData = async (symbol) => {
  // Check if it's a crypto symbol
  if (isCrypto(symbol)) {
    return await fetchCryptoData(symbol);
  }
  
  // For stocks, use the existing mock data (would need paid API for real data)
  const fundamentalData = {
    symbol,
    name: getMockCompanyName(symbol),
    financials: getMockFinancials(symbol),
    news: getMockNews(symbol),
    sentimentAnalysis: getMockSentimentAnalysis(symbol),
    ratings: getMockAnalystRatings(symbol),
    valuation: getMockValuation(symbol),
    peerComparison: getMockPeerComparison(symbol),
    updatedAt: new Date().toISOString(),
    isStockData: true,
    notice: 'Stock data requires API key. Showing sample data.'
  };
  
  return fundamentalData;
};

// Mock data helpers
const getMockCompanyName = (symbol) => {
  const companies = {
    'AAPL': 'Apple Inc.',
    'MSFT': 'Microsoft Corporation',
    'AMZN': 'Amazon.com, Inc.',
    'GOOGL': 'Alphabet Inc.',
    'TSLA': 'Tesla, Inc.',
    'META': 'Meta Platforms, Inc.',
    'NVDA': 'NVIDIA Corporation',
    'BTC': 'Bitcoin',
    'ETH': 'Ethereum'
  };
  
  return companies[symbol] || `${symbol} Corporation`;
};

const getMockFinancials = (symbol) => {
  // Generate slightly different values based on symbol to make it look realistic
  const hash = symbol.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const randomFactor = hash % 20 / 10;
  
  return {
    revenue: {
      value: (15.7 * (1 + randomFactor)).toFixed(2),
      change: ((randomFactor - 0.5) * 15).toFixed(2),
      unit: 'B',
      period: 'Q1 2025'
    },
    earnings: {
      value: (2.3 * (1 + randomFactor)).toFixed(2),
      change: ((randomFactor - 0.3) * 25).toFixed(2),
      unit: 'B',
      period: 'Q1 2025'
    },
    eps: {
      value: (1.42 * (1 + randomFactor)).toFixed(2),
      change: ((randomFactor - 0.4) * 18).toFixed(2),
      unit: 'USD',
      period: 'Q1 2025'
    },
    freeCashFlow: {
      value: (3.8 * (1 + randomFactor)).toFixed(2),
      change: ((randomFactor - 0.2) * 20).toFixed(2),
      unit: 'B',
      period: 'Q1 2025'
    },
    debtToEquity: {
      value: (0.42 * (1 + randomFactor)).toFixed(2),
      change: ((randomFactor - 0.6) * 8).toFixed(2),
      isGoodWhenNegative: true,
      period: 'Q1 2025'
    },
    grossMargin: {
      value: (41.5 * (1 + randomFactor/3)).toFixed(2),
      change: ((randomFactor - 0.5) * 5).toFixed(2),
      unit: '%',
      period: 'Q1 2025'
    },
    operatingMargin: {
      value: (24.8 * (1 + randomFactor/2)).toFixed(2),
      change: ((randomFactor - 0.4) * 7).toFixed(2),
      unit: '%',
      period: 'Q1 2025'
    }
  };
};

const getMockNews = (symbol) => {
  // Different news based on symbol
  const newsBySymbol = {
    'AAPL': [
      { title: 'Apple Unveils New AI Features for iOS 19', date: '2025-05-16', sentiment: 0.8, source: 'TechCrunch' },
      { title: 'Apple Services Revenue Hits All-Time High', date: '2025-05-14', sentiment: 0.7, source: 'CNBC' },
      { title: 'Supply Chain Issues Could Impact New iPhone Production', date: '2025-05-10', sentiment: -0.4, source: 'Bloomberg' }
    ],
    'MSFT': [
      { title: 'Microsoft Cloud Growth Exceeds Analyst Expectations', date: '2025-05-15', sentiment: 0.9, source: 'Reuters' },
      { title: 'Microsoft Expands AI Partnership with OpenAI', date: '2025-05-12', sentiment: 0.6, source: 'The Verge' },
      { title: 'New Windows Update Introduces Advanced Security Features', date: '2025-05-09', sentiment: 0.5, source: 'ZDNet' }
    ],
    'TSLA': [
      { title: 'Tesla Gigafactory Berlin Reaches Production Milestone', date: '2025-05-16', sentiment: 0.7, source: 'Electrek' },
      { title: 'New Tesla Model 2 Coming Earlier Than Expected', date: '2025-05-13', sentiment: 0.8, source: 'CleanTechnica' },
      { title: 'Tesla FSD Subscription Price Increases by 20%', date: '2025-05-08', sentiment: -0.3, source: 'Automotive News' }
    ],
    'BTC': [
      { title: 'Bitcoin ETF Inflows Reach New Monthly Record', date: '2025-05-16', sentiment: 0.9, source: 'CoinDesk' },
      { title: 'Bitcoin Mining Difficulty Adjusts Higher After Hash Rate Surge', date: '2025-05-12', sentiment: 0.4, source: 'Bitcoin Magazine' },
      { title: 'Central Banks Continue Bitcoin Accumulation Trend', date: '2025-05-10', sentiment: 0.7, source: 'Cointelegraph' }
    ]
  };
  
  // Default news if symbol not found
  const defaultNews = [
    { title: `${symbol} Reports Strong Quarterly Earnings`, date: '2025-05-15', sentiment: 0.6, source: 'Bloomberg' },
    { title: `${symbol} CEO Discusses Growth Strategy at Conference`, date: '2025-05-13', sentiment: 0.4, source: 'CNBC' },
    { title: `Industry Analysis: What's Next for ${symbol}?`, date: '2025-05-10', sentiment: 0.2, source: 'Seeking Alpha' }
  ];
  
  return newsBySymbol[symbol] || defaultNews;
};

const getMockSentimentAnalysis = (symbol) => {
  // Generate sentiment based on hash of symbol
  const hash = symbol.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const baseValue = (hash % 10) / 10;
  
  return {
    overallSentiment: {
      score: (0.3 + baseValue * 0.5).toFixed(2),
      change: ((baseValue - 0.5) * 0.3).toFixed(2)
    },
    sources: {
      news: (0.2 + baseValue * 0.6).toFixed(2),
      social: (0.3 + baseValue * 0.4).toFixed(2),
      earnings: (0.25 + baseValue * 0.5).toFixed(2),
      analyst: (0.35 + baseValue * 0.4).toFixed(2)
    },
    trends: [
      { date: '2025-04-17', value: (0.2 + baseValue * 0.4).toFixed(2) },
      { date: '2025-04-24', value: (0.3 + baseValue * 0.3).toFixed(2) },
      { date: '2025-05-01', value: (0.4 + baseValue * 0.2).toFixed(2) },
      { date: '2025-05-08', value: (0.5 + baseValue * 0.2).toFixed(2) },
      { date: '2025-05-15', value: (0.6 + baseValue * 0.3).toFixed(2) }
    ]
  };
};

const getMockAnalystRatings = (symbol) => {
  // Generate ratings based on hash of symbol
  const hash = symbol.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const buyWeight = (hash % 15) / 10;
  
  const totalAnalysts = 24;
  const buy = Math.floor(totalAnalysts * (0.4 + buyWeight * 0.3));
  const hold = Math.floor(totalAnalysts * (0.3 - buyWeight * 0.1));
  const sell = totalAnalysts - buy - hold;
  
  return {
    consensus: buy > (hold + sell) ? 'Buy' : buy < sell ? 'Sell' : 'Hold',
    targetPrice: {
      average: ((100 + hash % 50) * 1.2).toFixed(2),
      high: ((100 + hash % 50) * 1.5).toFixed(2),
      low: ((100 + hash % 50) * 0.9).toFixed(2),
      current: ((100 + hash % 50) * 1.1).toFixed(2)
    },
    distribution: {
      buy,
      hold,
      sell
    },
    priceTargets: [
      { firm: 'Morgan Stanley', target: ((100 + hash % 50) * 1.3).toFixed(2), rating: 'Buy' },
      { firm: 'Goldman Sachs', target: ((100 + hash % 50) * 1.25).toFixed(2), rating: buy > 15 ? 'Buy' : 'Hold' },
      { firm: 'JP Morgan', target: ((100 + hash % 50) * 1.15).toFixed(2), rating: buy > 18 ? 'Buy' : 'Hold' },
      { firm: 'Bank of America', target: ((100 + hash % 50) * 1.1).toFixed(2), rating: buy > 12 ? 'Buy' : 'Sell' }
    ]
  };
};

const getMockValuation = (symbol) => {
  // Generate valuation metrics based on symbol
  const hash = symbol.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const baseValue = (hash % 20) / 10;
  
  return {
    peRatio: (20 + baseValue * 15).toFixed(2),
    forwardPe: (18 + baseValue * 12).toFixed(2),
    pegRatio: (1.2 + baseValue * 0.8).toFixed(2),
    priceToSales: (4.5 + baseValue * 3).toFixed(2),
    priceToBook: (3.2 + baseValue * 2).toFixed(2),
    enterpriseValue: `${(150 + baseValue * 100).toFixed(1)}B`,
    evToRevenue: (5.8 + baseValue * 3).toFixed(2),
    evToEbitda: (15 + baseValue * 10).toFixed(2)
  };
};

const getMockPeerComparison = (symbol) => {
  const peersByIndustry = {
    'AAPL': ['MSFT', 'GOOGL', 'META', 'AMZN'],
    'MSFT': ['GOOGL', 'AAPL', 'AMZN', 'META'],
    'TSLA': ['F', 'GM', 'TM', 'RIVN'],
    'BTC': ['ETH', 'SOL', 'BNB', 'XRP'],
    'NVDA': ['AMD', 'INTC', 'TSM', 'MU']
  };
  
  const peers = peersByIndustry[symbol] || ['AAPL', 'MSFT', 'GOOGL', 'AMZN'];
  
  return peers.map(peer => {
    const hash = peer.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const random = (hash % 20) / 10;
    
    return {
      symbol: peer,
      name: getMockCompanyName(peer),
      peRatio: (20 + random * 15).toFixed(2),
      marketCap: `${(200 + random * 800).toFixed(1)}B`,
      priceChange: ((random - 0.5) * 20).toFixed(2)
    };
  });
};

// This service would handle real communication with the nodes in a production app
const FundamentalNodeService = {
  getFundamentalAnalysis: async (symbol) => {
    try {
      return await fetchFundamentalData(symbol);
    } catch (error) {
      console.error('Error fetching fundamental data:', error);
      throw error;
    }
  }
};

export default FundamentalNodeService;
